import React, { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { InviteGigsafeUserModal } from 'src/components/gigsafe-user/invite-form/InviteGigsafeUserModal'
import { GigsafeRolesModal } from 'src/components/gigsafe-user/role-form/GigsafeRolesModal'
import { GigsafeUsersTable } from 'src/components/gigsafe-user/users-table/GigsafeUsersTable'
import { GigsafeUsersTableToolbar } from 'src/components/gigsafe-user/users-table/GigsafeUsersTableToolbar'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import {
  AdminFetchMarkets,
  ClearGigsafeUserState,
  CreateGigsafeRole,
  FetchGigsafeRolePermissions,
  FetchGigsafeRoles,
  FetchGigsafeUsers,
  InviteGigsafeUser,
  UpdateGigsafeRole,
} from 'src/constants/actionTypes'
import { gigsafeUserPageSizeOptions } from 'src/constants/gigsafe-user/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  CreateGigsafeRolePayload,
  InviteGigsafeUserPayload,
  UpdateGigsafeRolePayload,
} from 'src/interfaces/gigsafeUser'
import { getGigsafeRolesError, getGigsafeUsersError } from 'src/selectors/gigsafeUser'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

const CustomerUsersPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const dispatch = useDispatch()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { isAuthenticated } = useAuthSession()
  // ENG-1207 part 2. Roles (list + assignable permissions) are `customer_roles_view`; without it
  // those GETs are skipped, the Roles button is hidden and the roles error banner cannot appear.
  const canViewRoles = useHasPermission(InternalPermission.CustomerRolesView)
  const canManageRoles = useHasPermission(InternalPermission.CustomerRolesManage)
  // The invite POST is `customer_invites_manage`, and its required Role select is fed by the
  // roles GET — without `customer_roles_view` the form could never be submitted, so Invite needs
  // both (same rule as spec §7.5's "Assign to invoice…").
  const canManageInvites = useHasPermission(InternalPermission.CustomerInvitesManage)
  const canInviteUsers = canManageInvites && canViewRoles
  // The invite form's market select; without `markets_view` it has no options ("All markets"
  // still works).
  const canViewMarkets = useHasPermission(InternalPermission.MarketsView)

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [isInviteOpen, setIsInviteOpen] = useState<boolean>(false)
  const [isRolesOpen, setIsRolesOpen] = useState<boolean>(false)
  const { paginationModel, limit, offset, setPaginationModel } = useGridPagination(
    gigsafeUserPageSizeOptions,
  )

  const usersError = useSelector(getGigsafeUsersError)
  const rolesError = useSelector(getGigsafeRolesError)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // This route reuses the same component instance across a customerId-only navigation
  // (`/customers/:customerId` → CustomerLayout → this `users` child), so every piece of
  // customer-scoped state has to be dropped explicitly or it bleeds into the next customer's
  // view: the slice itself, the id of a role created for the previous customer, and whatever
  // the admin had half-typed into either form (both are remounted by bumping their reset keys).
  // Pagination is deliberately NOT reset — it is the admin's own view preference, not data.
  useEffect(() => {
    dispatch(ClearGigsafeUserState())
    setIsInviteOpen(false)
    setIsRolesOpen(false)
  }, [customerId, dispatch])

  // Clearing on a customer change is not enough on its own: the submit handlers below await
  // their dispatches and this component survives the navigation, so a request issued for
  // customer A can resolve AFTER the effect above has run and write A's result onto B's page.
  // Each continuation compares its captured id against this live ref before applying anything.
  const currentCustomerIdRef = useRef<string | undefined>(customerId)

  useEffect(() => {
    currentCustomerIdRef.current = customerId
  }, [customerId])

  // Users. Reruns on every page change; the saga's takeLatest discards superseded runs. Held
  // until the internal session is confirmed, so no request goes out without a token to carry.
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(FetchGigsafeUsers.request({ partnerId: customerId, limit, offset }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, limit, offset, refreshToken])

  // Roles and the assignable permission list — neither takes a page, so this only reruns on a
  // customer change, a session change, or a manual refresh.
  useEffect(() => {
    if (!isAuthenticated || !customerId || !canViewRoles) {
      return
    }

    asyncDispatchWithErrorToast(FetchGigsafeRoles.request({ partnerId: customerId }))
    asyncDispatchWithErrorToast(FetchGigsafeRolePermissions.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, canViewRoles, refreshToken])

  // Markets for the invite form.
  useEffect(() => {
    if (!isAuthenticated || !customerId || !canViewMarkets) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchMarkets.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, canViewMarkets, refreshToken])

  const handleOpenInvite = useCallback(() => {
    setIsInviteOpen(true)
  }, [])

  const handleCloseInvite = useCallback(() => {
    setIsInviteOpen(false)
  }, [])

  const handleOpenRoles = useCallback(() => {
    setIsRolesOpen(true)
  }, [])

  const handleCloseRoles = useCallback(() => {
    setIsRolesOpen(false)
  }, [])

  const handleInvite = useCallback(
    async (payload: Omit<InviteGigsafeUserPayload, 'partnerId'>) => {
      if (!customerId) {
        return
      }

      const result = await asyncDispatchWithErrorToast<unknown>(
        InviteGigsafeUser.request({ ...payload, partnerId: customerId }),
      )

      if (!result) {
        return
      }

      // Dropped if the admin moved to another customer while this was in flight — otherwise
      // this continuation reports customer A's invite on customer B's page and refreshes B.
      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      snackbar?.enqueueSnackbar('Invite sent', { variant: 'success' })
      // Closing unmounts the modal, which is what resets the form for the next invite.
      setIsInviteOpen(false)
      handleRefresh()
    },
    [asyncDispatchWithErrorToast, customerId, snackbar, handleRefresh],
  )

  const handleCreateRole = useCallback(
    async (payload: Omit<CreateGigsafeRolePayload, 'partnerId'>) => {
      if (!customerId) {
        return
      }

      const result = await asyncDispatchWithErrorToast<unknown>(
        CreateGigsafeRole.request({ ...payload, partnerId: customerId }),
      )

      if (!result) {
        return
      }

      // Dropped if the admin moved to another customer while this was in flight — otherwise this
      // continuation reports customer A's role on customer B's page and refreshes B.
      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      snackbar?.enqueueSnackbar('Role created', { variant: 'success' })
      // Closing unmounts the modal, which is what resets the form for the next role.
      setIsRolesOpen(false)
      handleRefresh()
    },
    [asyncDispatchWithErrorToast, customerId, snackbar, handleRefresh],
  )

  const handleUpdateRole = useCallback(
    async (payload: Omit<UpdateGigsafeRolePayload, 'partnerId'>) => {
      if (!customerId) {
        return
      }

      const result = await asyncDispatchWithErrorToast<unknown>(
        UpdateGigsafeRole.request({ ...payload, partnerId: customerId }),
      )

      if (!result) {
        return
      }

      // Dropped if the admin moved to another customer while this was in flight.
      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      snackbar?.enqueueSnackbar('Role updated', { variant: 'success' })
      setIsRolesOpen(false)
      handleRefresh()
    },
    [asyncDispatchWithErrorToast, customerId, snackbar, handleRefresh],
  )

  return (
    <Fragment>
      <div data-testid="admin-customer-users-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div css={tw`min-w-0`}>
            <Typography variant="h2">Users</Typography>
            <Typography variant="body2" css={tw`mt-1 text-shades-500`}>
              This customer&apos;s own staff logins, the invites that create them, and the roles
              those invites assign.
            </Typography>
          </div>
          {/* Two header actions: inviting is the tab's CTA, roles is the occasional setup task
              behind it — so roles is the grey secondary, not a second dark button. */}
          <div css={tw`flex shrink-0 flex-row items-center gap-2`}>
            {canViewRoles ? (
              <Button
                size="small"
                color="grey"
                className={tw`px-4`}
                leftIcon={<GSIcon iconName="gs_key_1" size={20} css={tw`text-shades-1000`} />}
                onClick={handleOpenRoles}
                data-testid="gigsafe-roles-manage-button"
              >
                Roles
              </Button>
            ) : null}
            {canInviteUsers ? (
              <Button
                size="small"
                color="dark"
                className={tw`px-4`}
                leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
                onClick={handleOpenInvite}
                data-testid="gigsafe-users-invite-button"
              >
                Invite user
              </Button>
            ) : null}
          </div>
        </div>

        {usersError ? (
          <Typography
            variant="body2"
            css={tw`mb-4 rounded-lg bg-critical-50 p-3 text-critical-500`}
            data-testid="gigsafe-users-error"
          >
            {usersError}
          </Typography>
        ) : null}

        {/* The roles read feeds both modals' selects, so its failure is reported on the page
            rather than inside a modal the admin may never open. */}
        {rolesError ? (
          <Typography
            variant="body2"
            css={tw`mb-4 rounded-lg bg-critical-50 p-3 text-critical-500`}
            data-testid="gigsafe-roles-error"
          >
            {rolesError}
          </Typography>
        ) : null}

        <GigsafeUsersTableToolbar onRefresh={handleRefresh} />
        <GigsafeUsersTable
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </div>

      <InviteGigsafeUserModal
        isOpen={isInviteOpen}
        onClose={handleCloseInvite}
        onSubmit={handleInvite}
      />
      <GigsafeRolesModal
        isOpen={isRolesOpen}
        isReadOnly={!canManageRoles}
        onClose={handleCloseRoles}
        onCreate={handleCreateRole}
        onUpdate={handleUpdateRole}
      />
    </Fragment>
  )
}

export const CustomerUsersPage = memo(CustomerUsersPageComponent)
